import { z } from "zod";

/**
 * PREC-3870 — what one uploaded XLSX source produced, for the after-enrichment XLS Statistics
 * modal. Every figure is **derived at read time** from rows that already exist, so a source
 * enriched long before this shipped reports the same numbers as one enriched today.
 */

/**
 * The file as it arrived: rows in, rows that became source records, and the gap between them.
 *
 * The gap is `notImported`, deliberately not "duplicates": a row can fail to become a source
 * record for several reasons, and a source whose rows were reset has none at all — which the
 * "duplicates" spelling rendered as "every row was a duplicate". The pre-upload box keeps the
 * word because it counts them from the sheet in the browser and genuinely knows.
 */
export const csvImportStatsSchema = z.object({
	rowsDetected: z.coerce.number(),
	rowsReady: z.coerce.number(),
	notImported: z.coerce.number(),
});

/**
 * Job-title verdicts for the titles this file carried. Note the label these count towards reads
 * "Job titles whitelisted", not "**New** job titles whitelisted": `job_title_screening` is a
 * workspace-wide store with no link to a file, so which titles were new *at the time this file ran*
 * is not recoverable without a snapshot nobody took. A truthful narrower label beats a wrong number.
 */
export const csvScreeningStatsSchema = z.object({
	whitelisted: z.coerce.number(),
	blacklisted: z.coerce.number(),
});

/**
 * Per-input-row terminal accounting for an uploaded **company** file, from the outcomes both
 * company scrapers stamp (`CompanyOutcome`). See docs/company-import-loss-analysis.md — this is
 * the web rendering that Phase 1 left as a follow-up.
 */
export const csvCompanyStatsSchema = z.object({
	found: z.coerce.number(),
	mergedInto: z.coerce.number(),
	notFound: z.coerce.number(),
	excluded: z.coerce.number(),
	pending: z.coerce.number(),
});

/**
 * The leads the file produced, in the same buckets — and with the same reason partition — the
 * list's own grouped Show dropdown renders. The two are one query with one extra predicate
 * (`fetchSourceLeadCounts`), so they can never disagree.
 */
const exclusionReasonCountSchema = z.object({
	inclusionStatus: z.string(),
	filterParam: z.string(),
	count: z.coerce.number(),
});

export const csvEnrichmentResultStatsSchema = z.object({
	found: z.coerce.number(),
	unsure: z.coerce.number(),
	notFound: z.coerce.number(),
	blacklist: z.coerce.number(),
	exclusionReasonCounts: z.array(exclusionReasonCountSchema),
});

export const csvEnrichmentStatisticsSchema = z.object({
	csvEnrichmentId: z.coerce.number(),
	fileName: z.string().nullish(),
	/** CSVEnrichmentType — decides whether the screening or the company group is shown. */
	entityType: z.string().nullish(),
	import: csvImportStatsSchema,
	/** Absent for a company upload: a company file carries no job titles to screen. */
	screening: csvScreeningStatsSchema.nullish(),
	/** Absent for a people upload: only company files carry per-row company outcomes. */
	companies: csvCompanyStatsSchema.nullish(),
	enrichment: csvEnrichmentResultStatsSchema,
});

export type CsvImportStats = z.infer<typeof csvImportStatsSchema>;
export type CsvScreeningStats = z.infer<typeof csvScreeningStatsSchema>;
export type CsvCompanyStats = z.infer<typeof csvCompanyStatsSchema>;
export type CsvEnrichmentResultStats = z.infer<typeof csvEnrichmentResultStatsSchema>;
export type CsvEnrichmentStatistics = z.infer<typeof csvEnrichmentStatisticsSchema>;
